<script setup lang="ts" generic="U extends ZodAny">
import type { ZodAny } from 'zod';
import { computed } from 'vue';
import type { Config, ConfigItem, Shape } from './interface';
import { DEFAULT_ZOD_HANDLERS, INPUT_COMPONENTS } from './constant';
import useDependencies from './dependencies';

const props = defineProps<{
  fieldName: string;
  shape: Shape;
  config?: ConfigItem | Config<U>;
}>();

function isValidConfig(config: any): config is ConfigItem {
  return !!config?.component;
}

const delegatedProps = computed(() => {
  if (['ZodObject', 'ZodArray'].includes(props.shape?.type))
    return { schema: props.shape?.schema };
  return undefined;
});

const { isDisabled, isHidden, isRequired, overrideOptions } = useDependencies(
  props.fieldName,
);

const component = computed(() => {
  if (isValidConfig(props.config)) {
    if (typeof props.config.component === 'string') {
      return INPUT_COMPONENTS[props.config.component];
    } else {
      return props.config.component;
    }
  } else {
    const handler = DEFAULT_ZOD_HANDLERS[props.shape.type];
    if (handler) {
      return INPUT_COMPONENTS[handler];
    } else {
      return INPUT_COMPONENTS.string;
    }
  }
});
</script>

<template>
  <component
    :is="component"
    v-if="!isHidden"
    :field-name="fieldName"
    :label="shape.schema?.description"
    :required="isRequired || shape.required"
    :options="overrideOptions || shape.options"
    :disabled="isDisabled"
    :config="config"
    v-bind="delegatedProps"
  >
    <slot />
  </component>
</template>
